import { useGT } from 'gt-react';
import { ChartBarIcon } from '@phosphor-icons/react';
import { reportNumberFormatOptions } from '@erp/shared-types';
import { defineComponentType } from '../registry';
import { NumberFormatFields } from '../format/NumberFormatFields';
import { CategorySortFields } from './CategorySortFields';
import type { BarChartComponent } from '../types';
import {
  CategoryValueFields,
  ConfigRow,
  ToggleRow,
  valueByCategoryTitle,
  ChartFrame,
  useXYChart,
  useChartFit,
} from './shared';
import { aggregateByCategory, sortCategories } from './chartRows';
import { BuiltinChart } from './BuiltinChart';
import type { BuiltinChartRequest } from './viz/request';

function BarChartRenderer({ component }: { component: BarChartComponent }) {
  const { detail } = useChartFit();
  const { dataset, ready, xKey, yKey, xLabel, yLabel } = useXYChart(component);

  // Group rows by the x-axis category and sum the y-values, so each category
  // renders one bar reflecting its total (re-grouped whenever either axis
  // changes), then sort and keep the top N.
  const chartRows = sortCategories(
    aggregateByCategory(dataset?.rows, component.xAxisKey, component.yAxisKey),
    component.xAxisKey,
    component.yAxisKey,
    component.sort,
    component.topN,
  );
  const valueLabels = component.showLabels && detail.tickLabels;
  const request: BuiltinChartRequest = {
    kind: 'bar',
    options: {
      data: chartRows,
      x: xKey,
      y: yKey,
      xTitle: xLabel,
      yTitle: yLabel,
      // Already in its sort order, top N taken.
      keepOrder: true,
      valueFormat: reportNumberFormatOptions(component.valueFormat),
      valueLabels,
      grid: component.showGrid,
    },
  };
  return (
    <ChartFrame
      ready={ready}
      hasData={dataset !== null}
      icon={<ChartBarIcon className="h-8 w-8 text-muted-foreground/40" />}
    >
      <BuiltinChart request={request} />
    </ChartFrame>
  );
}

function BarChartConfigForm({
  component,
  onUpdate,
}: {
  component: BarChartComponent;
  onUpdate: (updated: BarChartComponent) => void;
}) {
  const gt = useGT();
  return (
    <div className="space-y-4">
      <CategoryValueFields
        component={component}
        onUpdate={onUpdate}
        xLabel={gt('X-axis (categories)')}
        yLabel={gt('Y-axis (values)')}
      />
      <CategorySortFields
        idPrefix="bar"
        sort={component.sort}
        topN={component.topN}
        onChange={(next) =>
          onUpdate({ ...component, sort: next.sort, topN: next.topN ?? 0 })
        }
      />
      <ConfigRow label={gt('Values')}>
        <NumberFormatFields
          idPrefix="bar-values"
          value={component.valueFormat}
          onChange={(valueFormat) => onUpdate({ ...component, valueFormat })}
        />
      </ConfigRow>
      <ToggleRow
        label={gt('Show grid lines')}
        id="bar-grid"
        checked={component.showGrid}
        onCheckedChange={(v) => onUpdate({ ...component, showGrid: v })}
      />
      <ToggleRow
        label={gt('Show value labels')}
        id="bar-labels"
        checked={component.showLabels}
        onCheckedChange={(v) => onUpdate({ ...component, showLabels: v })}
      />
    </div>
  );
}

export function BarChartPalettePreview() {
  return (
    <div className="flex h-24 items-center justify-center text-foreground">
      <svg viewBox="0 0 44 36" className="h-16 w-auto" fill="none">
        <line
          x1="7"
          y1="4"
          x2="7"
          y2="30"
          stroke="currentColor"
          strokeWidth="1"
          strokeOpacity="0.3"
        />
        <line
          x1="7"
          y1="30"
          x2="40"
          y2="30"
          stroke="currentColor"
          strokeWidth="1"
          strokeOpacity="0.3"
        />
        <rect x="10" y="17" width="6" height="13" rx="1" fill="currentColor" />
        <rect x="19" y="10" width="6" height="20" rx="1" fill="currentColor" />
        <rect x="28" y="21" width="6" height="9" rx="1" fill="currentColor" />
        <rect x="37" y="14" width="6" height="16" rx="1" fill="currentColor" />
      </svg>
    </div>
  );
}

export const barChartDefinition = defineComponentType<BarChartComponent>({
  type: 'bar-chart',
  label: 'Bar chart',
  defaultTitle: valueByCategoryTitle('xAxisKey', 'yAxisKey', 'Bar chart'),
  description:
    'Dataset-bound vertical bars: groups rows by the xAxisKey column and sums the numeric yAxisKey column per group. sort: none|value-desc|value-asc|label-asc|label-desc; topN keeps the first N after sorting (0 = all); valueFormat {style: number|currency|percent, decimals?, currency?, compact?}.',
  columnRefFields: ['xAxisKey', 'yAxisKey'],
  version: 1,
  make: (id) => ({
    type: 'bar-chart',
    id,
    xAxisKey: null,
    yAxisKey: null,
    showGrid: true,
    showLabels: false,
    sort: 'none',
    topN: 0,
  }),
  Renderer: BarChartRenderer,
  ConfigForm: BarChartConfigForm,
  Preview: BarChartPalettePreview,
});
